/**
 * RecipeEditorDialog — Fiche recette (édition/création) en overlay Dialog
 * réutilisable. Extrait depuis `pages/Recipes.jsx` (Feb 2026) pour permettre
 * l'ouverture d'une recette depuis :
 *  - la page Recettes (mode "gestion")
 *  - la fiche produit → rubrique « Recettes à optimiser » (mode "consultation
 *    contextuelle", sans naviguer vers /plats)
 *
 * Le composant est totalement autonome : il gère son propre state formulaire,
 * charge les produits si besoin, sauvegarde via l'API et empile un
 * `ProductDetail` interne quand l'utilisateur clique un ingrédient — la
 * page/fiche d'origine reste montée en arrière-plan.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CategoryIcon } from "./CategoryIcon";
import ProductDetail from "./ProductDetail";
import { eur, VAT_RATES, ALLERGENS, unitLabel } from "../lib/format";

export const RECIPE_CATEGORIES = ["Entrée", "Plat", "Dessert", "Accompagnement", "Cocktail", "Boisson", "Autre"];

export const EMPTY_RECIPE = {
  name: "", category: "Plat", portions: 1, selling_price: 0, is_ttc_sale: true, vat_rate: 10,
  monthly_volume: 0, target_margin_pct: 70, packaging_cost: 0, platform_commission_pct: 0,
  other_variable_cost: 0, ingredients: [], allergens: [], traces: [],
};

// ---------- Unités & conversions (aligné backend calculations.py) ----------
const TO_BASE = { kg: 1000, g: 1, L: 1000, cl: 10, ml: 1, "unité": 1, "u": 1, "Mesure": 1 };
const UNIT_FAMILY = { kg: "weight", g: "weight", L: "volume", l: "volume", cl: "volume", ml: "volume", "unité": "unit", "u": "unit", "Mesure": "unit" };
const _WEIGHT_TO_KG = { kg: 1, g: 0.001 };
const _VOLUME_TO_L = { L: 1, l: 1, cl: 0.01, ml: 0.001, dl: 0.1 };
const _UNIT_SYNS = new Set(["u", "unité", "unite", "unit", "pièce", "piece", "pcs", "pc"]);

function convertQtyToBase(qty, fromUnit, toBase) {
  const s = String(fromUnit || "").trim();
  if (toBase === "kg") return _WEIGHT_TO_KG[s] !== undefined ? qty * _WEIGHT_TO_KG[s] : null;
  if (toBase === "L")  { const f = _VOLUME_TO_L[s] ?? _VOLUME_TO_L[s.toLowerCase()]; return f !== undefined ? qty * f : null; }
  if (toBase === "u")  return _UNIT_SYNS.has(s.toLowerCase()) ? qty : null;
  return null;
}

function productTotals(product) {
  if (!product) return { family: "unit", total_base: 0 };
  const w = Number(product.total_weight_g || 0);
  if (w > 0) return { family: "weight", total_base: w };
  const v = Number(product.total_volume_ml || 0);
  if (v > 0) return { family: "volume", total_base: v };
  const cv = Number(product.content_value || 0);
  const cu = product.content_unit;
  const packs = Number(product.pack_count || product.pack_units || 1);
  if (cv > 0 && cu) {
    if (["kg", "g"].includes(cu)) return { family: "weight", total_base: cv * TO_BASE[cu] * packs };
    const lc = String(cu).toLowerCase();
    if (["l", "cl", "ml"].includes(lc)) {
      const key = lc === "l" ? "L" : lc;
      return { family: "volume", total_base: cv * TO_BASE[key] * packs };
    }
  }
  const u = Number(product.total_units || 0);
  if (u > 0) return { family: "unit", total_base: u };
  const packedQty = Number(product.purchase_quantity || 1) * Number(product.pack_units || 1);
  const pu = product.purchase_unit;
  return { family: UNIT_FAMILY[pu] || "unit", total_base: packedQty * (TO_BASE[pu] || 1) };
}

function productUnitProfile(p) {
  if (!p) return { family: "unit", suggested: "u", compat: ["u", "Mesure", "g", "cl"] };
  // Feb 2026 §« MEMES BONS PRIX PARTOUT » — Priorité ABSOLUE au
  // `ref_base_unit` canonique (le vrai contrat recette). Ne pas
  // pré-sélectionner « pièce » pour une tomate en kg juste parce que
  // `purchase_unit` traîne une valeur héritée d'une saisie manuelle.
  const rbu = (p.ref_base_unit || "").trim();
  if (rbu === "kg") return { family: "weight", suggested: "g", compat: ["g", "kg", "u", "Mesure"] };
  if (rbu === "L") return { family: "volume", suggested: "cl", compat: ["cl", "ml", "L", "u", "Mesure"] };
  if (rbu === "u") return { family: "unit", suggested: "u", compat: ["u", "Mesure", "g", "kg", "cl", "ml", "L"] };
  const tu = Number(p.total_units || 0);
  const tw = Number(p.total_weight_g || 0);
  const tv = Number(p.total_volume_ml || 0);
  if (tu > 0) return { family: "unit",   suggested: "u", compat: ["u", "Mesure", "g", "kg", "cl", "ml", "L"] };
  if (tw > 0) return { family: "weight", suggested: "g", compat: ["g", "kg", "u", "Mesure"] };
  if (tv > 0) return { family: "volume", suggested: "cl", compat: ["cl", "ml", "L", "u", "Mesure"] };
  const pu = (p.purchase_unit || "").toLowerCase();
  if (["kg", "g"].includes(pu))       return { family: "weight", suggested: "g",  compat: ["kg", "g", "u", "Mesure"] };
  if (["l", "cl", "ml"].includes(pu)) return { family: "volume", suggested: "cl", compat: ["L", "cl", "ml", "u", "Mesure"] };
  return { family: "unit", suggested: "u", compat: ["u", "Mesure", "g", "cl"] };
}

function ingredientCost(product, quantity, unit) {
  if (!product || !quantity) return 0;
  if (String(unit || "").trim().toLowerCase() === "mesure") return 0;
  const refPrice = Number(product.ref_price_per_base || 0);
  const refBase = product.ref_base_unit;
  if (refPrice > 0 && refBase) {
    const yieldPct = Number(product.yield_percent ?? 100) || 100;
    const lossPct = Number(product.loss_percent ?? 0) || 0;
    const usable = (yieldPct / 100) * (1 - lossPct / 100);
    if (usable <= 0) return 0;
    const converted = convertQtyToBase(Number(quantity), unit, refBase);
    if (converted !== null) return (refPrice / usable) * converted;
    const usedFam = UNIT_FAMILY[unit] || (["u","unité","unite","unit","pièce","piece","pcs","pc"].includes(String(unit||"").toLowerCase()) ? "unit" : null);
    let refBaseQty = 0;
    if (refBase === "u") refBaseQty = Number(product.total_units || 0);
    else if (refBase === "kg") refBaseQty = Number(product.total_weight_g || 0) / 1000;
    else if (refBase === "L") refBaseQty = Number(product.total_volume_ml || 0) / 1000;
    if (refBaseQty <= 0) return 0;
    const lineTotal = refPrice * refBaseQty;
    if (usedFam === "weight") { const t = Number(product.total_weight_g || 0); if (t <= 0) return 0; return (lineTotal * Number(quantity) * (TO_BASE[unit] || 1) / t) / usable; }
    if (usedFam === "volume") { const t = Number(product.total_volume_ml || 0); if (t <= 0) return 0; return (lineTotal * Number(quantity) * (TO_BASE[unit] || 1) / t) / usable; }
    if (usedFam === "unit")   { const t = Number(product.total_units || 0);    if (t <= 0) return 0; return (lineTotal * Number(quantity) / t) / usable; }
    return 0;
  }
  const priceHT = product.price_is_ttc
    ? Number(product.purchase_price || 0) / (1 + Number(product.vat_rate || 0) / 100)
    : Number(product.purchase_price || 0);
  if (priceHT <= 0) return 0;
  const usedFamily = UNIT_FAMILY[unit] || (["unité","unite","unit","u","pièce","piece","pcs","pc"].includes(String(unit || "").toLowerCase()) ? "unit" : "unit");
  let total = null;
  if (usedFamily === "weight") { const w = Number(product.total_weight_g || 0); if (w > 0) total = w; }
  else if (usedFamily === "volume") { const v = Number(product.total_volume_ml || 0); if (v > 0) total = v; }
  else { const u = Number(product.total_units || 0); if (u > 0) total = u; }
  if (total === null) {
    const cv = Number(product.content_value || 0);
    const cu = product.content_unit;
    const packs = Number(product.pack_count || product.pack_units || 1);
    if (cv > 0 && cu) {
      if (usedFamily === "weight" && TO_BASE[cu] && UNIT_FAMILY[cu] === "weight") total = cv * TO_BASE[cu] * packs;
      else if (usedFamily === "volume" && TO_BASE[cu] && UNIT_FAMILY[cu] === "volume") total = cv * TO_BASE[cu] * packs;
    }
    if (total === null) {
      const { family: legacyFam, total_base } = productTotals(product);
      if (legacyFam === usedFamily && total_base > 0) total = total_base;
    }
  }
  if (!total || total <= 0) return 0;
  const used = Number(quantity) * (TO_BASE[unit] || 1);
  return (priceHT / total) * used;
}

const KNOWN_UNITS = new Set(["kg", "g", "L", "l", "cl", "ml", "dl", "unité", "unite", "unit", "u", "pièce", "piece", "pcs", "pc", "mesure"]);

function ingredientCostReason(product, quantity, unit) {
  if (!product) return "Produit introuvable";
  const q = Number(quantity || 0);
  if (q <= 0) return "Quantité manquante";
  if (String(unit || "").trim().toLowerCase() === "mesure") return null;
  const priceHT = product.price_is_ttc
    ? Number(product.purchase_price || 0) / (1 + Number(product.vat_rate || 0) / 100)
    : Number(product.purchase_price || 0);
  if (priceHT <= 0) return "Prix de référence manquant";
  const u = String(unit || "").trim();
  if (!KNOWN_UNITS.has(u) && !KNOWN_UNITS.has(u.toLowerCase())) return "Unité non reconnue";
  if (ingredientCost(product, q, unit) > 0) return null;
  const hasAny = Boolean(product.total_weight_g || product.total_volume_ml || product.total_units || product.content_value);
  return hasAny ? "Conversion impossible" : "Conditionnement à vérifier";
}

function mergeAllergensFromProduct(formLike, product) {
  if (!product) return formLike;
  const prodAllergens = product.allergens || [];
  const prodTraces = product.traces || [];
  const sources = { ...(formLike.allergen_sources || {}) };
  const traceSources = { ...(formLike.trace_sources || {}) };
  for (const code of prodAllergens) {
    const set = new Set(sources[code] || []);
    set.add(product.id); sources[code] = Array.from(set);
  }
  for (const code of prodTraces) {
    const set = new Set(traceSources[code] || []);
    set.add(product.id); traceSources[code] = Array.from(set);
  }
  const allergens = Array.from(new Set([...(formLike.allergens || []), ...prodAllergens])).sort();
  const traces = Array.from(new Set([...(formLike.traces || []), ...prodTraces])).sort();
  return { ...formLike, allergens, traces, allergen_sources: sources, trace_sources: traceSources };
}

// ---------- Section allergènes INCO 1169/2011 ----------
function AllergensSection({ value, suggestions = [], allergenSources = {}, productsById = {}, onChange }) {
  const allergens = value.allergens || [];
  const toggleAllergen = (code) => {
    if (allergens.includes(code)) {
      const srcIds = allergenSources[code] || [];
      if (srcIds.length > 0) {
        const names = srcIds.map((id) => productsById[id]?.name).filter(Boolean).join(", ");
        const ok = window.confirm(
          `L'allergène « ${ALLERGENS.find((a) => a.code === code)?.label || code} » a été détecté depuis ${srcIds.length > 1 ? "les ingrédients" : "l'ingrédient"} : ${names || "un produit"}.\n\nÊtes-vous certain de vouloir le retirer manuellement de la recette ? Cette décision sera écrasée si vous rechargez la fiche ingrédient.`,
        );
        if (!ok) return;
      }
      onChange({ allergens: allergens.filter((c) => c !== code) });
    } else onChange({ allergens: [...allergens, code] });
  };
  const suggestedPending = suggestions.filter((c) => !allergens.includes(c));
  const acceptAllSuggestions = () => onChange({ allergens: Array.from(new Set([...allergens, ...suggestions])) });

  return (
    <section aria-labelledby="rf-allerg" data-testid="recipe-allergens-section">
      <h4 id="rf-allerg" className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-3">Allergènes (INCO 1169/2011)</h4>
      {suggestedPending.length > 0 && (
        <div className="mb-3 rounded-md border border-[#FFD79E] bg-[#FFF6E5] p-3 text-[12px] text-[#7A4B00]" data-testid="recipe-allergens-suggest-banner">
          <div className="font-bold mb-1">Allergènes suggérés à partir des ingrédients</div>
          <div>Vérification requise avant publication.</div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {suggestedPending.map((c) => {
              const a = ALLERGENS.find((x) => x.code === c);
              return <span key={c} className="inline-flex items-center gap-1 bg-white/70 text-[#7A4B00] border border-[#FFD79E] rounded-full px-2 py-0.5 text-[11px] font-semibold">{a?.label || c}</span>;
            })}
            <button type="button" onClick={acceptAllSuggestions} data-testid="recipe-allergens-accept-all" className="ml-1 text-[11px] font-bold underline decoration-2 underline-offset-2 text-[#0B1F33] hover:text-[#1769E0]">Tout confirmer</button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {ALLERGENS.map((a) => {
          const active = allergens.includes(a.code);
          const suggested = suggestions.includes(a.code);
          const srcNames = (allergenSources[a.code] || []).map((id) => productsById[id]?.name).filter(Boolean);
          const fromIngredient = srcNames.length > 0;
          const tooltip = fromIngredient ? `${a.desc}\n\nDétecté depuis : ${srcNames.join(", ")}` : a.desc;
          return (
            <button key={a.code} type="button" onClick={() => toggleAllergen(a.code)} data-testid={`recipe-allergen-${a.code}`} title={tooltip}
              className={`text-left px-2.5 py-1.5 rounded-md border text-[12px] font-semibold transition-colors relative ${
                active && fromIngredient ? "bg-[#DCEBFF] text-[#0B1F33] border-[#B4D1F9]"
                : active ? "bg-[#FF9F1C] text-[#0B1F33] border-[#FF9F1C]"
                : suggested ? "bg-[#FFF6E5] text-[#7A4B00] border-[#FFD79E] hover:bg-[#FFEBBF]"
                : "bg-white text-[#0B1F33] border-[#CDD7E3] hover:bg-[#F4F5EF]"}`}>
              <span className="font-mono-num text-[10px] mr-1 opacity-70">{a.code}</span>
              {a.label}
              {active && fromIngredient && <span className="ml-1 inline-block align-middle text-[9px] font-bold uppercase tracking-widest text-[#1769E0]" title="Détecté automatiquement depuis un ingrédient">auto</span>}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ============ Composant principal ============
export default function RecipeEditorDialog({
  open,
  recipeId = null,          // string | null   — null = création
  initialRecipe = null,     // objet recette optionnel (évite un fetch)
  initialProducts = null,   // liste produits optionnelle (évite un fetch)
  onOpenChange,
  onSaved,                  // (recipe) => void — appelé après sauvegarde OK
}) {
  const [form, setForm] = useState(EMPTY_RECIPE);
  const [products, setProducts] = useState(initialProducts || []);
  const [showExtras, setShowExtras] = useState(false);
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [detailProductId, setDetailProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydratation à chaque ouverture / changement de recette
  useEffect(() => {
    if (!open) return;
    setShowExtras(false); setIngredientSearch("");
    if (initialRecipe) setForm(initialRecipe);
    else if (recipeId) {
      setLoading(true);
      api.getRecipe(recipeId)
        .then((r) => setForm(r))
        .catch(() => toast.error("Impossible de charger la recette"))
        .finally(() => setLoading(false));
    } else setForm(EMPTY_RECIPE);
    // Toujours rafraîchir la liste produits à l'ouverture pour disposer
    // des derniers prix / conditionnements.
    if (initialProducts && initialProducts.length > 0) setProducts(initialProducts);
    api.products().then(setProducts).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipeId, initialRecipe]);

  const addIngredient = (product) => {
    const existingIdx = form.ingredients.findIndex((i) => i.product_id === product.id);
    if (existingIdx >= 0) {
      const merge = window.confirm(`« ${product.name} » est déjà dans la recette. Fusionner les quantités ?`);
      if (!merge) return;
      const list = [...form.ingredients];
      list[existingIdx] = { ...list[existingIdx], quantity: Number(list[existingIdx].quantity || 0) + 1 };
      setForm({ ...form, ingredients: list });
      return;
    }
    const profile = productUnitProfile(product);
    setForm((prev) => mergeAllergensFromProduct({
      ...prev,
      ingredients: [...prev.ingredients, { product_id: product.id, quantity: 0, unit: profile.suggested }],
    }, product));
    setIngredientSearch("");
  };

  const rmIng = (i) => {
    const remaining = form.ingredients.filter((_, k) => k !== i);
    const productsById = Object.fromEntries(products.map((p) => [p.id, p]));
    const manual = new Set(form.allergens_manual || []);
    const manualT = new Set(form.traces_manual || []);
    const recomputeSources = (kind) => {
      const src = {};
      for (const ing of remaining) {
        const p = productsById[ing.product_id];
        if (!p) continue;
        for (const c of (p[kind] || [])) (src[c] = src[c] || new Set()).add(p.id);
      }
      return Object.fromEntries(Object.entries(src).map(([k, v]) => [k, Array.from(v)]));
    };
    const allergenSources = recomputeSources("allergens");
    const traceSources = recomputeSources("traces");
    const allergens = Array.from(new Set([...Object.keys(allergenSources), ...manual])).sort();
    const traces = Array.from(new Set([...Object.keys(traceSources), ...manualT])).sort();
    setForm({ ...form, ingredients: remaining, allergens, traces, allergen_sources: allergenSources, trace_sources: traceSources });
  };

  const setIngField = (i, key, v) => {
    const list = [...form.ingredients];
    list[i] = { ...list[i], [key]: v };
    setForm({ ...form, ingredients: list });
  };

  const allergenSuggestions = useMemo(() => {
    const productsById = Object.fromEntries(products.map((p) => [p.id, p]));
    const codes = new Set();
    for (const ing of form.ingredients) {
      const p = productsById[ing.product_id];
      for (const c of (p?.allergens || [])) codes.add(c);
    }
    return Array.from(codes).sort();
  }, [form.ingredients, products]);

  const save = async () => {
    if (!form.name || !form.selling_price) { toast.error("Nom et prix requis"); return; }
    setSaving(true);
    const payload = {
      ...form,
      portions: Number(form.portions), selling_price: Number(form.selling_price),
      vat_rate: Number(form.vat_rate), monthly_volume: Number(form.monthly_volume),
      target_margin_pct: Number(form.target_margin_pct),
      packaging_cost: Number(form.packaging_cost || 0),
      platform_commission_pct: Number(form.platform_commission_pct || 0),
      other_variable_cost: Number(form.other_variable_cost || 0),
      allergens: form.allergens || [], traces: form.traces || [],
      allergens_manual: form.allergens_manual || [], traces_manual: form.traces_manual || [],
      allergen_sources: form.allergen_sources || {}, trace_sources: form.trace_sources || {},
      ingredients: form.ingredients.map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity), unit: i.unit })),
    };
    try {
      const saved = form.id
        ? await api.updateRecipe(form.id, payload)
        : await api.createRecipe(payload);
      toast.success(form.id ? "Fiche recette mise à jour" : "Fiche recette créée");
      window.dispatchEvent(new CustomEvent("rentascan:usage-changed", { detail: { resource: "recipes" } }));
      window.dispatchEvent(new CustomEvent("rentascan:recipe-updated", { detail: { id: form.id || saved?.id } }));
      onSaved?.(saved || form);
      onOpenChange?.(false);
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const productMatches = useMemo(() => {
    const q = ingredientSearch.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => ((p.name || "") + " " + (p.brand || "") + " " + (p.supplier_ref || "")).toLowerCase().includes(q)).slice(0, 8);
  }, [products, ingredientSearch]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="p-0 gap-0 overflow-hidden bg-white
                     max-w-full w-screen h-[100dvh] max-h-[100dvh] rounded-none border-0
                     sm:max-w-3xl sm:w-[95vw] sm:h-auto sm:max-h-[92vh] sm:rounded-lg sm:border
                     flex flex-col
                     [&>button.absolute.right-4.top-4]:hidden"
          data-testid="recipe-form-dialog"
        >
          <DialogHeader className="px-5 sm:px-6 py-4 border-b border-[#CDD7E3] shrink-0 flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-[17px] font-heading font-extrabold text-[#0B1F33]">
              {form.id ? "Modifier la fiche recette" : "Nouvelle fiche recette"}
            </DialogTitle>
            <button onClick={() => onOpenChange?.(false)} data-testid="recipe-form-close"
              className="w-8 h-8 rounded-md hover:bg-[#F4F5EF] flex items-center justify-center text-[#5E6875]" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </DialogHeader>

          {loading ? (
            <div className="p-10 text-center text-[13px] text-[#5E6875]">Chargement de la recette…</div>
          ) : (
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5 space-y-6">
              {/* --- Section 1 · Informations générales --- */}
              <section aria-labelledby="rf-general">
                <h4 id="rf-general" className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-3">Informations générales</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-[12px] mb-1">Nom de la recette</Label>
                    <Input data-testid="recipe-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. Burrata coulis de tomate" />
                  </div>
                  <div>
                    <Label className="text-[12px] mb-1">Catégorie</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="recipe-form-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{RECIPE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[12px] mb-1">Nombre de portions</Label>
                    <Input type="number" min="1" value={form.portions} onChange={(e) => setForm({ ...form, portions: e.target.value })} data-testid="recipe-form-portions" />
                  </div>
                </div>
              </section>

              {/* --- Section 2 · Prix & rentabilité --- */}
              <section aria-labelledby="rf-price">
                <h4 id="rf-price" className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-3">Prix &amp; rentabilité</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[12px] mb-1">Prix de vente</Label>
                    <div className="flex items-center gap-2">
                      <Input data-testid="recipe-form-price" type="number" step="0.01" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[11px] font-bold text-[#5E6875]">HT</span>
                        <Switch checked={form.is_ttc_sale} onCheckedChange={(v) => setForm({ ...form, is_ttc_sale: v })} />
                        <span className="text-[11px] font-bold text-[#0B1F33]">TTC</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[12px] mb-1">TVA</Label>
                    <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{VAT_RATES.map((r) => <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[12px] mb-1">Objectif de marge (%)</Label>
                    <Input type="number" value={form.target_margin_pct} onChange={(e) => setForm({ ...form, target_margin_pct: e.target.value })} data-testid="recipe-form-target" />
                  </div>
                </div>
              </section>

              {/* --- Section 3 · Ingrédients --- */}
              <section aria-labelledby="rf-ing">
                <h4 id="rf-ing" className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-3">Ingrédients</h4>
                <div className="relative mb-3">
                  <Input value={ingredientSearch} onChange={(e) => setIngredientSearch(e.target.value)}
                    placeholder="Rechercher et ajouter un produit (nom, marque, réf. fournisseur)…"
                    data-testid="recipe-ing-search" />
                  {productMatches.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#CDD7E3] rounded-md shadow-lg max-h-[240px] overflow-y-auto">
                      {productMatches.map((p) => {
                        const profile = productUnitProfile(p);
                        const lastPrice = Number(p.purchase_price || 0);
                        return (
                          <button key={p.id} type="button" onClick={() => addIngredient(p)} data-testid={`recipe-ing-add-${p.id}`}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F4F5EF] text-left">
                            <CategoryIcon category={p.category} size={36} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-semibold text-[#0B1F33] truncate">{p.name}</div>
                          <div className="text-[11px] text-[#5E6875]">
                            {(() => {
                              // Feb 2026 §« recherche recette affiche total ligne »
                              // Prix affiché = prix de RÉFÉRENCE normalisé (€/kg,
                              // €/L, €/pièce) et JAMAIS le `purchase_price`
                              // (dernier total de ligne facture). Cela évitait
                              // « 65,60 € » pour 32 burratas au lieu de « 2,05 € /pc ».
                              const refPrice = Number(p.ref_price_per_base || 0);
                              const refUnit = p.ref_base_unit;
                              const unitSuffix = refUnit === "u" ? "pièce" : refUnit;
                              const showRef = refPrice > 0 && refUnit;
                              // Masque « unité recette : X » si égal à l'unité fournisseur.
                              const showSuggested = profile.suggested && profile.suggested !== p.purchase_unit;
                              return (
                                <>
                                  {p.purchase_quantity} {p.purchase_unit}
                                  {showSuggested && <> · unité recette : <b>{profile.suggested}</b></>}
                                  {showRef && <> · {eur(refPrice, refPrice < 1 ? 3 : 2)} / {unitSuffix}</>}
                                </>
                              );
                            })()}
                          </div>
                            </div>
                            <Plus className="w-4 h-4 text-[#1769E0]" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {form.ingredients.length === 0 && <p className="text-[12px] text-[#5E6875] italic">Aucun ingrédient. Utilisez la recherche pour en ajouter.</p>}
                {form.ingredients.length > 0 && (
                  <div className="border border-[#CDD7E3] rounded-md overflow-hidden">
                    <div className="grid grid-cols-[1fr_80px_90px_80px_36px] gap-2 px-3 py-2 bg-[#F4F5EF] text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">
                      <span>Produit</span><span className="text-right">Quantité</span><span>Unité</span><span className="text-right">Coût</span><span></span>
                    </div>
                    {form.ingredients.map((ing, i) => {
                      const product = products.find((p) => p.id === ing.product_id);
                      const profile = productUnitProfile(product);
                      const cost = ingredientCost(product, ing.quantity, ing.unit);
                      const costReason = cost === 0 ? ingredientCostReason(product, ing.quantity, ing.unit) : null;
                      return (
                        <div key={i} className="grid grid-cols-[1fr_80px_90px_80px_36px] gap-2 px-3 py-2 border-t border-[#CDD7E3] items-center" data-testid={`recipe-ing-row-${i}`}>
                          <button type="button" onClick={() => product && setDetailProductId(product.id)} disabled={!product}
                            title={product ? "Ouvrir la fiche produit (historique, fournisseur, recettes liées)" : "Produit introuvable"}
                            data-testid={`recipe-ing-open-${i}`}
                            className="flex items-center gap-2 min-w-0 text-left rounded-md px-1 py-0.5 -mx-1 -my-0.5 hover:bg-[#F4F5EF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9F1C] disabled:opacity-70 disabled:cursor-not-allowed">
                            <CategoryIcon category={product?.category} size={28} />
                            <span className={`text-[13px] font-semibold truncate ${product ? "text-[#0B1F33] hover:underline underline-offset-2" : "text-[#5E6875] italic"}`}>
                              {product?.name || "Produit introuvable"}
                            </span>
                          </button>
                          <Input type="number" step="0.1" value={ing.quantity} onChange={(e) => setIngField(i, "quantity", e.target.value)} className="!h-9 text-right tabular-nums" data-testid={`recipe-ing-qty-${i}`} />
                          <Select value={ing.unit} onValueChange={(v) => setIngField(i, "unit", v)}>
                            <SelectTrigger className="!h-9" data-testid={`recipe-ing-unit-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{profile.compat.map((u) => <SelectItem key={u} value={u}>{unitLabel(u)}</SelectItem>)}</SelectContent>
                          </Select>
                          {costReason ? (
                            <span title={costReason} data-testid={`recipe-ing-cost-reason-${i}`} className="text-[11px] font-semibold text-right text-[#B36B00] leading-tight">{costReason}</span>
                          ) : (
                            <span className="text-[13px] font-mono-num tabular-nums text-right text-[#0B1F33] font-bold" data-testid={`recipe-ing-cost-${i}`}>{eur(cost, 3)}</span>
                          )}
                          <button onClick={() => rmIng(i)} data-testid={`recipe-ing-remove-${i}`} className="w-8 h-8 rounded-md hover:bg-[#FDECEC] text-[#D84343] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* --- Section 4 · Extras --- */}
              <section>
                <button type="button" onClick={() => setShowExtras((v) => !v)} data-testid="recipe-form-extras-toggle"
                  className="w-full flex items-center justify-between py-2 text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] hover:text-[#0B1F33]">
                  <span>Informations complémentaires</span>
                  {showExtras ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showExtras && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <div><Label className="text-[12px] mb-1">Volume mensuel estimé (portions)</Label><Input type="number" value={form.monthly_volume} onChange={(e) => setForm({ ...form, monthly_volume: e.target.value })} /></div>
                    <div><Label className="text-[12px] mb-1">Emballage par portion (€)</Label><Input type="number" step="0.01" value={form.packaging_cost} onChange={(e) => setForm({ ...form, packaging_cost: e.target.value })} /></div>
                    <div><Label className="text-[12px] mb-1">Commission plateforme (%)</Label><Input type="number" step="0.01" value={form.platform_commission_pct} onChange={(e) => setForm({ ...form, platform_commission_pct: e.target.value })} /></div>
                    <div><Label className="text-[12px] mb-1">Autres coûts variables (€)</Label><Input type="number" step="0.01" value={form.other_variable_cost} onChange={(e) => setForm({ ...form, other_variable_cost: e.target.value })} /></div>
                  </div>
                )}
              </section>

              {/* --- Section 5 · Allergènes --- */}
              <AllergensSection
                value={{ allergens: form.allergens || [] }}
                suggestions={allergenSuggestions}
                allergenSources={form.allergen_sources || {}}
                productsById={Object.fromEntries(products.map((p) => [p.id, p]))}
                onChange={(patch) => {
                  const next = { ...form, ...patch };
                  if (patch.allergens) {
                    const fromIng = new Set(Object.keys(form.allergen_sources || {}));
                    next.allergens_manual = (patch.allergens || []).filter((c) => !fromIng.has(c));
                  }
                  setForm(next);
                }}
              />
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-[#CDD7E3] px-5 sm:px-6 py-3 bg-white flex items-center justify-end gap-2 shrink-0">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="recipe-form-cancel">Annuler</Button>
            <Button data-testid="recipe-form-save" onClick={save} disabled={saving || loading} className="bg-[#2878FF] hover:bg-[#1769E0] text-white">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fiche produit ouverte depuis un ingrédient — reste superposée au
          formulaire pour ne pas perdre les modifs en cours (Feb 2026). */}
      {detailProductId && (
        <ProductDetail
          productId={detailProductId}
          onClose={() => setDetailProductId(null)}
          onChanged={() => { api.products().then(setProducts).catch(() => {}); }}
        />
      )}
    </>
  );
}
